import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import withRoleAccess from '../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { 
  Row, 
  Col, 
  Card, 
  Statistic, 
  Typography, 
  List, 
  Avatar,
  Tag,
  Button,
  Spin,
  Empty,
  Space,
  Timeline,
  Badge,
  Alert
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  BookOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined
} from '@ant-design/icons';
import './Dashboard.css';

const { Text, Title } = Typography;

function AdminDashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({});
  const [todayClasses, setTodayClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classesLoading, setClassesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'timeline'
  const [statusFilter] = useState('all');

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch statistics data
  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [studentsResponse, teachersResponse, classesResponse] = await Promise.all([
        axios.get(`${API_URL}/students/count`, { headers }),
        axios.get(`${API_URL}/teachers/count`, { headers }),
        axios.get(`${API_URL}/class-groups/count`, { headers })
      ]);

      setStats({
        totalStudents: studentsResponse.data.total_students,
        totalTeachers: teachersResponse.data.total_teachers,
        totalClasses: classesResponse.data.total_classes,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setError(t('fetchStatsError'));
    }
  }, [API_URL, t]);

  // Fetch today's classes
  const fetchTodayClasses = useCallback(async () => {
    try {
      setClassesLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API_URL}/classes/today`, { headers });
      
      // Sort classes by time
      const sortedClasses = response.data.sort((a, b) => {
        return a.schedule_time.localeCompare(b.schedule_time);
      });
      
      setTodayClasses(sortedClasses);
    } catch (error) {
      console.error('Error fetching today\'s classes:', error);
      setError(t('fetchTodayClassesError'));
      setTodayClasses([]);
    } finally {
      setClassesLoading(false);
    }
  }, [API_URL, t]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchStats(), fetchTodayClasses()]);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [fetchStats, fetchTodayClasses]);

  // Get filtered classes based on status
  const getFilteredClasses = () => {
    if (statusFilter === 'all') return todayClasses;
    return todayClasses.filter(cls => cls.status === statusFilter);
  };

  // Get status color and icon
  const getStatusConfig = (status) => {
    const configs = {
      'approved': { color: 'green', icon: <CheckCircleOutlined />, text: 'Approved' },
      'active': { color: 'processing', icon: <PlayCircleOutlined />, text: 'Active' },
      'ongoing': { color: 'processing', icon: <PlayCircleOutlined />, text: 'Ongoing' },
      'cancelled': { color: 'error', icon: <ExclamationCircleOutlined />, text: 'Cancelled' },
      'pending': { color: 'warning', icon: <PauseCircleOutlined />, text: 'Pending' },
      'default': { color: 'default', icon: <ClockCircleOutlined />, text: 'Unknown' }
    };
    return configs[status] || configs.default;
  };

  // Format time for display
  const formatTime = (timeString) => {
    return moment(timeString, 'HH:mm').format('HH:mm');
  };

  // Calculate end time
  const calculateEndTime = (startTime, duration) => {
    if (!duration) return 'N/A';
    
    let durationMinutes = 0;
    if (typeof duration === 'string') {
      if (duration.includes('h') && duration.includes('m')) {
        const hourMatch = duration.match(/(\d+)h/);
        const minuteMatch = duration.match(/(\d+)m/);
        durationMinutes = (hourMatch ? parseInt(hourMatch[1]) * 60 : 0) + 
                         (minuteMatch ? parseInt(minuteMatch[1]) : 0);
      } else {
        durationMinutes = parseInt(duration) || 60;
      }
    }
    
    return moment(startTime, 'HH:mm').add(durationMinutes, 'minutes').format('HH:mm');
  };

  // Get class statistics
  const getClassStats = () => {
    const total = todayClasses.length;
    const completed = todayClasses.filter(cls => cls.status === 'approved').length;
    const active = todayClasses.filter(cls => ['active', 'ongoing'].includes(cls.status)).length;
    const pending = todayClasses.filter(cls => cls.status === 'pending').length;

    return { total, completed, active, pending };
  };

  const classStats = getClassStats();
  const filteredClasses = getFilteredClasses();

  // Render class item for list view
  const renderClassItem = (classItem) => {
    const statusConfig = getStatusConfig(classItem.status);
    const endTime = calculateEndTime(classItem.schedule_time, classItem.schedule_hour);

    return (
      <List.Item key={classItem.classid}>
        <List.Item.Meta
          avatar={
            <Avatar 
              style={{ 
                backgroundColor: statusConfig.color === 'success' ? '#52c41a' :
                               statusConfig.color === 'processing' ? '#1890ff' :
                               statusConfig.color === 'error' ? '#ff4d4f' :
                               statusConfig.color === 'warning' ? '#faad14' : '#d9d9d9'
              }}
            >
              {classItem.subject_name?.[0] || 'C'}
            </Avatar>
          }
          title={
            <Space wrap>
              <Text strong>{classItem.subject_name || 'Unknown Subject'}</Text>
              <Tag color={statusConfig.color} icon={statusConfig.icon}>
                {statusConfig.text}
              </Tag>
            </Space>
          }
          description={
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Space wrap>
                <Text type="secondary">
                  <BookOutlined /> {classItem.class_code || 'N/A'}
                </Text>
                <Text type="secondary">
                  <UserOutlined /> {classItem.nickname || 'No Teacher'}
                </Text>
              </Space>
              <Space wrap>
                <Text type="secondary">
                  <ClockCircleOutlined /> {formatTime(classItem.schedule_time)} - {endTime}
                </Text>
                {classItem.classroom_number && (
                  <Text type="secondary">
                    <EnvironmentOutlined /> Room {classItem.classroom_number}
                  </Text>
                )}
              </Space>
            </Space>
          }
        />
      </List.Item>
    );
  };

  // Render timeline view
  const renderTimelineView = () => {
    const timelineItems = filteredClasses.map(classItem => {
      const statusConfig = getStatusConfig(classItem.status);
      const endTime = calculateEndTime(classItem.schedule_time, classItem.schedule_hour);

      return {
        key: classItem.classid,
        dot: statusConfig.icon,
        color: statusConfig.color === 'processing' ? 'blue' :
               statusConfig.color === 'success' ? 'green' :
               statusConfig.color === 'error' ? 'red' :
               statusConfig.color === 'warning' ? 'orange' : 'gray',
        children: (
          <div>
            <div style={{ marginBottom: 8 }}>
              <Space>
                <Text strong>{formatTime(classItem.schedule_time)} - {endTime}</Text>
                <Tag color={statusConfig.color} size="small">
                  {statusConfig.text}
                </Tag>
              </Space>
            </div>
            <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
              {classItem.subject_name || 'Unknown Subject'}
            </Title>
            <Space wrap size={[8, 4]}>
              <Text type="secondary">{classItem.class_code}</Text>
              <Text type="secondary">• {classItem.nickname || 'No Teacher'}</Text>
              {classItem.classroom_number && (
                <Text type="secondary">• Room {classItem.classroom_number}</Text>
              )}
            </Space>
          </div>
        )
      };
    });

    return (
      <Timeline 
        mode="left"
        items={timelineItems}
        style={{ marginTop: 16 }}
      />
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        padding: '24px'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 24px' }}>
      {/* Header */}
      <Row style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card>
            <Row align="middle" justify="space-between">
              <Col xs={24} sm={18}>
                <Text type="primary" style={{ fontSize: '24px' }}>
                  {t('Today\'s overview')} - {moment().format('dddd, DD MMMM YYYY')}
                </Text>
              </Col>
              <Col xs={24} sm={6} style={{ textAlign: 'right', marginTop: window.innerWidth < 576 ? '12px' : '0' }}>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={() => {
                    fetchStats();
                    fetchTodayClasses();
                  }}
                  loading={classesLoading}
                >
                  {t('Refresh')}
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Statistics Section */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={8} lg={6}>
          <Card size="small">
            <Statistic
              title={t('Total Students')}
              value={stats.totalStudents}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff', fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={6}>
          <Card size="small">
            <Statistic
              title={t('Total Teachers')}
              value={stats.totalTeachers}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#52c41a', fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={6}>
          <Card size="small">
            <Statistic
              title={t('Total Courses')}
              value={stats.totalClasses}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#faad14', fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={24} lg={6}>
          <Card size="small">
            <Statistic
              title={t('Today\'s Classes')}
              value={classStats.total}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#722ed1', fontSize: '20px' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Today's Classes Status Overview */}
      {todayClasses.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title={t('Completed')}
                value={classStats.completed}
                valueStyle={{ color: '#52c41a', fontSize: '18px' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title={t('Active')}
                value={classStats.active}
                valueStyle={{ color: '#1890ff', fontSize: '18px' }}
                prefix={<PlayCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title={t('Pending')}
                value={classStats.pending}
                valueStyle={{ color: '#faad14', fontSize: '18px' }}
                prefix={<PauseCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Classes List Section */}
      <Row>
        <Col span={24}>
          <Card 
            title={
              <Space>
                <ClockCircleOutlined />
                <Text strong>{t('Today\'s Classes Schedule')}</Text>
                <Badge count={filteredClasses.length} />
              </Space>
            }
            extra={
              <Space wrap>
                <Button.Group size="small">
                  <Button 
                    type={viewMode === 'list' ? 'primary' : 'default'}
                    onClick={() => setViewMode('list')}
                    icon={<FilterOutlined />}
                  >
                    {t('List')}
                  </Button>
                  <Button 
                    type={viewMode === 'timeline' ? 'primary' : 'default'}
                    onClick={() => setViewMode('timeline')}
                    icon={<ClockCircleOutlined />}
                  >
                    {t('Timeline')}
                  </Button>
                </Button.Group>
              </Space>
            }
            loading={classesLoading}
          >
            {error && (
              <Alert 
                message={error} 
                type="error" 
                style={{ marginBottom: 16 }} 
                closable 
              />
            )}
            
            {filteredClasses.length === 0 ? (
              <Empty 
                description={
                  statusFilter === 'all' 
                    ? t('No classes scheduled for today')
                    : t('No classes found for selected status')
                }
                image={Empty.SIMPLE_MODE}
              />
            ) : (
              <div>
                {viewMode === 'list' ? (
                  <List
                    itemLayout="horizontal"
                    dataSource={filteredClasses}
                    renderItem={renderClassItem}
                    size="small"
                    style={{ 
                      maxHeight: window.innerWidth < 768 ? 'none' : '600px', 
                      overflowY: window.innerWidth < 768 ? 'visible' : 'auto' 
                    }}
                  />
                ) : (
                  <div style={{ 
                    maxHeight: window.innerWidth < 768 ? 'none' : '600px', 
                    overflowY: window.innerWidth < 768 ? 'visible' : 'auto',
                    paddingRight: '8px'
                  }}>
                    {renderTimelineView()}
                  </div>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default withRoleAccess(AdminDashboard, ['admin', 'superadmin']);